import { inject, onBeforeUnmount, provide } from 'vue';

export interface EditorValidator {
  addValidation(v: EditControlValidation): () => void; // returns an unsubscribe
  removeValidation(v: EditControlValidation): void;
  clear(): void;
  list(): readonly EditControlValidation[];
  validate(): { valid: boolean; invalidFields: (string | undefined)[] };
  validateAsync(): Promise<{ valid: boolean; invalidFields: (string | undefined)[] }>;
}

export function createEditorValidator(): EditorValidator {
  const validations: EditControlValidation[] = [];

  function addValidation(v: EditControlValidation) {
    validations.push(v);
    return () => removeValidation(v);
  }

  function removeValidation(v: EditControlValidation) {
    const i = validations.indexOf(v);
    if (i !== -1) validations.splice(i, 1);
  }

  function clear() {
    validations.length = 0;
  }

  function list() {
    return validations as readonly EditControlValidation[];
  }

  function validate() {
    const invalidFields: (string | undefined)[] = [];
    let valid = true;
    for (const val of validations) {
      const res = safeCall(val);
      valid = valid && res;
      if (!res) invalidFields.push(val.config.column?.title);
    }
    return { valid, invalidFields };
  }

  async function validateAsync() {
    const results = await Promise.all(validations.map(v => Promise.resolve(safeCall(v))));
    const invalidFields = validations
      .map((v, i) => (results[i] ? undefined : v.config.column?.title))
      .filter(Boolean) as (string | undefined)[];
    const valid = results.every(Boolean);
    return { valid, invalidFields };
  }

  function safeCall(v: EditControlValidation): boolean {
    try {
      const r = v.config.validate?.();
      return r !== false; // treat undefined/true as valid, only false is invalid
    } catch (e) {
      console.error('validate() threw for', v.config.column?.title, e);
      return false;
    }
  }

  return { addValidation, removeValidation, clear, list, validate, validateAsync };
}

const EditorValidatorKey = Symbol('EditorValidator');

export function provideEditorValidator(ev: EditorValidator) {
  provide(EditorValidatorKey, ev);
}

export function useEditorValidator() {
  const ev = inject<EditorValidator | null>(EditorValidatorKey, null);
  if (!ev) {console.warn("EventValidor not available!");}
  return ev;
}

export function useRegisterValidation(v: EditControlValidation) {
  const ev = useEditorValidator();
  if (ev) {
    const off = ev.addValidation(v);
    onBeforeUnmount(off);
  }
}